/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable prettier/prettier */
import { useEffect, useMemo, useState } from "react";
import ReactTooltip from "react-tooltip";
import {
  ModalContent,
  Card,
  Common,
  Icon,
  CustomScrollbar,
} from "@/components";
import { GoogleMap } from "./Map";
import { OptimizedReviewItem } from "./OptimizedReviewItem";
import { OptimizedReviewDetail } from "./OptimizedReviewDetail";
import {
  ModalOptimizedCherryPick,
  CrossZoneManifestLine,
} from "./ModalOptimizedCherryPick";
import clsx from "clsx";
import {
  manifest,
  manifestLine,
  optimizeInfo,
  reoptimizeResponse,
  solution,
  storageAddress,
  unserved,
} from "../models";
import { useAppSelector, useAppDispatch } from "@/sharedStore/hooks";
import {
  deliveryManifest,
  storageAddressSelector,
  routificSettingsSelector,
} from "../providers/deliveryManifest/selector";
import {
  useSubmitOptimizeRoutes,
  useOptimizeRequest,
  useReoptimize,
} from "../services/deliveryOptimize";
import { SnackbarUtils } from "@/utils";
import { groupUnallocatedManifest, mergeManifestOptimize } from "../utils";
import { manifestIds } from "../configVariable";
import { useSaveEditingRoutes } from "../services/deliveryManifest";
import { useGetCapacityType } from "../providers/deliveryManifest/hooks";

interface Props {
  open: boolean;
  onClose: () => void;
  onDiscard: () => void;
  onSubmit: () => void;
  onMoveUnallocated: (manifestLine: manifestLine, route: manifest) => void;
  solutions: solution[];
  unserved: unserved[];
  manifests: manifest[];
  optimizeInfo: optimizeInfo[];
  manuallyChanged: boolean;
  jobId?: string;
}

export const ModalOptimized = ({
  open,
  onClose,
  onDiscard,
  onSubmit,
  onMoveUnallocated,
  solutions,
  unserved,
  manifests,
  optimizeInfo,
  manuallyChanged,
  jobId,
}: Props) => {
  const submitOptimizeRoutes = useSubmitOptimizeRoutes();
  const saveManuallyOptimization = useSaveEditingRoutes();
  const optimizeRequest = useOptimizeRequest();
  const reoptimize = useReoptimize();
  const dispatch = useAppDispatch();

  const [isOpenDetail, setOpenDetail] = useState(false);
  const [detailData, setDetailData] = useState<manifest>({} as manifest);
  const [isCherryPickMode, setIsCherryPickMode] = useState(false);
  const [crossZoneLines, setCrossZoneLines] = useState<CrossZoneManifestLine[]>(
    []
  );
  const [isReoptimizing, setIsReoptimizing] = useState(false);
  const [reoptimizingRoutesCount, setReoptimizingRoutesCount] = useState(0);

  const storageAddress = useAppSelector(
    storageAddressSelector
  ) as storageAddress;

  const deliveryManifestData = useAppSelector(deliveryManifest);
  const routificSettings = useAppSelector(routificSettingsSelector);

  useEffect(() => {
    ReactTooltip.rebuild();
    return () => {
      ReactTooltip.hide();
    };
  }, []);

  // Detect cross-zone manifest lines
  const detectCrossZoneLines = useMemo(() => {
    const crossZones: CrossZoneManifestLine[] = [];
    const setting =
      deliveryManifestData?.accountInfo?.routeOptimisationAcceptRejectSetting;

    solutions.forEach((slt) => {
      const optimizedManifest = manifests.find((m) => m.id === slt.manifestId);
      if (!optimizedManifest) return;

      slt.visits.forEach((visit) => {
        const manifestLine = visit.manifestLine;

        // Check if manifest line has delivery tags and a default zone (first tag)
        if (manifestLine.deliveryTags && manifestLine.deliveryTags.length > 0) {
          const defaultZone = manifestLine.deliveryTags[0];

          // Check if the new route's delivery tags contain the default zone
          const newRouteTags = optimizedManifest.deliveryTags || [];
          const isInDefaultZone = newRouteTags.some(
            (tag) => tag === defaultZone
          );

          // Find the original manifest for this line (before optimization)
          const lineOriginalManifest = manifests.find((m) =>
            m.manifestLines.some((ml) => ml.id === manifestLine.id)
          );

          // If not in default zone and moved from another manifest
          if (
            !isInDefaultZone &&
            lineOriginalManifest &&
            lineOriginalManifest.id !== optimizedManifest.id
          ) {
            let targetManifest: manifest | undefined;

            if (setting === 1) {
              // By tag: find first route with the default zone (sorted by route name)
              const manifestsWithTag = manifests
                .filter((m) => m.deliveryTags?.includes(defaultZone))
                .sort((a, b) => a.route.name.localeCompare(b.route.name));

              targetManifest = manifestsWithTag[0] || undefined;
            } else {
              // By route (setting === 2): use original manifest
              targetManifest = lineOriginalManifest;
            }

            crossZones.push({
              manifestLine,
              originalManifest: lineOriginalManifest,
              newManifest: optimizedManifest,
              defaultZone,
              targetManifest,
              status: "pending",
            });
          }
        }
      });
    });

    return crossZones;
  }, [solutions, manifests, deliveryManifestData]);

  // Initialize cross-zone lines when first detected
  useEffect(() => {
    if (
      detectCrossZoneLines.length > 0 &&
      crossZoneLines.length === 0 &&
      !isCherryPickMode
    ) {
      setCrossZoneLines(detectCrossZoneLines);
    }
  }, [detectCrossZoneLines, crossZoneLines.length, isCherryPickMode]);

  // Check if cherry-pick button should be shown
  const shouldShowCherryPickButton = useMemo(() => {
    const setting =
      deliveryManifestData?.accountInfo?.routeOptimisationAcceptRejectSetting;
    return (setting === 1 || setting === 2) && detectCrossZoneLines.length > 0;
  }, [deliveryManifestData, detectCrossZoneLines]);

  const unallocatedManifest = useMemo(() => {
    return manifests.length && unserved?.length
      ? groupUnallocatedManifest(
          unserved.map((unserved) => {
            return {
              ...unserved.manifestLine,
              latitude: unserved.latitude,
              longitude: unserved.longitude,
            } as manifestLine;
          }),
          manifests[0].optimizingId
        )
      : undefined;
  }, [unserved, manifests]);

  const optimizedManifests = useMemo(() => {
    return manifests.map((manifest) => {
      const slt = solutions.find(
        (solution) => solution.manifestId === manifest.id
      ) as solution;
      const opInfo = optimizeInfo.find(
        (opInfoItem) => opInfoItem.manifestId === manifest.id
      ) as optimizeInfo;
      return mergeManifestOptimize(manifest, slt, opInfo);
    });
  }, [manifests, solutions, optimizeInfo]);

  const openDetailModal = (
    solution: solution,
    manifest: manifest,
    opInfo: optimizeInfo
  ) => {
    const manifestDetailData = mergeManifestOptimize(
      manifest,
      solution,
      opInfo
    ) as manifest;
    setDetailData(manifestDetailData);
    setOpenDetail(true);
  };

  const refetchManifests = () => {
    window.manifest_refreshManifestList();
  };

  const handleApply = () => {
    if (manuallyChanged) {
      saveManuallyOptimization.mutate(
        optimizedManifests.map((manifest) => {
          return {
            ...manifest,
            polylines: "",
            manifestLines: manifest.manifestLines.map((manifestLine) => {
              return {
                ...manifestLine,
                manifestId: manifest.id,
              };
            }),
          };
        }),
        {
          onSuccess: (res) => {
            if (res.errorMessage) {
              SnackbarUtils.error("Applying optimized routes failed.");
            } else {
              onSubmit();
              refetchManifests();
            }
          },
          onError: () => {
            SnackbarUtils.error("Applying optimized routes failed.");
          },
        }
      );
    } else {
      submitOptimizeRoutes.mutate(
        {
          solution: solutions,
          unserved,
          deliveryDate: manifests[0].deliveryDate,
        },
        {
          onSuccess: (res) => {
            if (res.errorMessage) {
              SnackbarUtils.error("Applying optimized routes failed.");
            } else {
              onSubmit();
              refetchManifests();
            }
          },
          onError: () => {
            SnackbarUtils.error("Applying optimized routes failed.");
          },
        }
      );
    }
  };

  const capacityType = useGetCapacityType()();

  // Handle accept new route for a manifest line
  const handleAcceptNew = (lineId: string) => {
    setCrossZoneLines((prev) =>
      prev.map((line) =>
        line.manifestLine.id === lineId
          ? { ...line, status: "accepted" as const }
          : line
      )
    );
  };

  // Handle keep original route for a manifest line
  const handleKeepOriginal = (lineId: string) => {
    setCrossZoneLines((prev) =>
      prev.map((line) =>
        line.manifestLine.id === lineId
          ? { ...line, status: "rejected" as const }
          : line
      )
    );
  };

  // Handle undo decision
  const handleUndo = (lineId: string) => {
    setCrossZoneLines((prev) =>
      prev.map((line) =>
        line.manifestLine.id === lineId
          ? { ...line, status: "pending" as const }
          : line
      )
    );
  };

  // Handle bulk accept new routes
  const handleBulkAcceptNew = (lineIds: string[]) => {
    setCrossZoneLines((prev) =>
      prev.map((line) =>
        lineIds.includes(line.manifestLine.id)
          ? { ...line, status: "accepted" as const }
          : line
      )
    );
  };

  // Handle bulk keep original routes
  const handleBulkKeepOriginal = (lineIds: string[]) => {
    setCrossZoneLines((prev) =>
      prev.map((line) =>
        lineIds.includes(line.manifestLine.id)
          ? { ...line, status: "rejected" as const }
          : line
      )
    );
  };

  // Handle cherry-pick button click
  const handleCherryPick = () => {
    setIsCherryPickMode(true);
  };

  // Handle save all in cherry-pick mode
  const handleSaveAllCherryPick = async () => {
    if (!jobId) {
      SnackbarUtils.error("Job ID is missing");
      return;
    }

    try {
      // Step 1: Create modified solutions based on accept/reject decisions
      const newSolutions = solutions.map((slt) => ({
        ...slt,
        visits: [...slt.visits],
      }));
      const rejectedLines = crossZoneLines.filter(
        (line) => line.status === "rejected"
      );
      const manifestsToReoptimize = new Set<string>();

      // Check if any lines were rejected
      const hasRejections = rejectedLines.length > 0;

      if (hasRejections) {
        // Step 2: Move rejected lines back to their target manifests
        rejectedLines.forEach((rejectedLine) => {
          // Remove from new manifest (where it was optimized to)
          const newManifestSolution = newSolutions.find(
            (s) => s.manifestId === rejectedLine.newManifest.id
          );
          if (newManifestSolution) {
            newManifestSolution.visits = newManifestSolution.visits.filter(
              (v) => v.manifestLine.id !== rejectedLine.manifestLine.id
            );
            manifestsToReoptimize.add(rejectedLine.newManifest.id);
          }

          // Add to target manifest (based on setting: by tag or by route)
          if (rejectedLine.targetManifest) {
            const targetManifestSolution = newSolutions.find(
              (s) => s.manifestId === rejectedLine.targetManifest?.id
            );
            if (targetManifestSolution) {
              // Find the original position or append at the end
              const originalPosition =
                rejectedLine.targetManifest.manifestLines.findIndex(
                  (ml) => ml.id === rejectedLine.manifestLine.id
                );

              const newVisit = {
                manifestLine: rejectedLine.manifestLine,
                position:
                  originalPosition >= 0
                    ? originalPosition + 1
                    : targetManifestSolution.visits.length + 1,
                arrivalTime: "",
                finishTime: "",
                longitude: rejectedLine.manifestLine.longitude || null,
                latitude: rejectedLine.manifestLine.latitude || null,
                duration: 0,
              };

              if (originalPosition >= 0) {
                targetManifestSolution.visits.splice(
                  originalPosition,
                  0,
                  newVisit
                );
              } else {
                targetManifestSolution.visits.push(newVisit);
              }

              manifestsToReoptimize.add(rejectedLine.targetManifest.id);
            }
          }
        });

        // Step 3: Update positions in all modified solutions
        newSolutions.forEach((slt) => {
          slt.visits = slt.visits.map((visit, index) => ({
            ...visit,
            position: index + 1,
            manifestLine: {
              ...visit.manifestLine,
              position: index,
            },
          }));
        });

        // Step 4: Mark all rejected lines as accepted
        // setCrossZoneLines(prev => prev.map(line =>
        //   line.status === 'rejected' ? { ...line, status: 'accepted' as const } : line
        // ));

        // Step 5: Reoptimize affected routes
        setIsReoptimizing(true);
        setReoptimizingRoutesCount(manifestsToReoptimize.size);

        // Prepare routes for reoptimization (only affected ones)
        const routesToReoptimize = Array.from(manifestsToReoptimize)
          .map((manifestId) =>
            optimizeInfo.find((op) => op.manifestId === manifestId)
          )
          .filter((opInfo): opInfo is optimizeInfo => opInfo !== undefined);

        const reoptimizeSolutions = newSolutions.filter(
          (slt) =>
            manifestsToReoptimize.has(slt.manifestId) && slt.visits.length > 0
        );

        let finalSolutions: solution[] = newSolutions;

        if (reoptimizeSolutions.length > 0) {
          // Call Reoptimize endpoint for each affected route individually
          const reoptimizePromises = Array.from(manifestsToReoptimize)
            .map((manifestId) => {
              const routeInfo = optimizeInfo.find(
                (op) => op.manifestId === manifestId
              );
              const routeSolution = newSolutions.find(
                (s) => s.manifestId === manifestId
              );

              if (
                !routeInfo ||
                !routeSolution ||
                routeSolution.visits.length === 0
              ) {
                return null;
              }

              return reoptimize.mutateAsync({
                solution: [routeSolution], // Single route
                unserved: unserved || [],
                optimizeSettings: {
                  ...routificSettings,
                  routes: [routeInfo], // Single route info
                  isApplyUnallocatedDoc: false,
                },
              });
            })
            .filter(
              (promise): promise is Promise<reoptimizeResponse> =>
                promise !== null
            );

          // Execute all reoptimize requests in parallel
          const reoptimizeResults = await Promise.all(reoptimizePromises);

          // Check if any request failed
          const failedResult = reoptimizeResults.find(
            (result) => result.status === "error"
          );
          if (failedResult) {
            SnackbarUtils.error(failedResult.error || "Reoptimization failed");
            setIsReoptimizing(false);
            return;
          }

          // Merge all individual results back into finalSolutions
          finalSolutions = newSolutions.map((slt) => {
            // Find the reoptimized solution for this manifest from any of the results
            for (const result of reoptimizeResults) {
              const reoptimizedSolution = result.solution?.find(
                (s: solution) => s.manifestId === slt.manifestId
              );
              if (reoptimizedSolution) {
                return reoptimizedSolution;
              }
            }
            return slt;
          });
        }

        // Update Redux state with final solutions
        // dispatch(updateOptimizeGroupSolutions({
        //   jobId,
        //   newSolutions: finalSolutions,
        // }));

        setIsReoptimizing(false);

        // Step 7: Apply the optimized result
        const manifestsToApply = finalSolutions
          .map((slt) => {
            const manifest = manifests.find((m) => m.id === slt.manifestId);
            const opInfo = optimizeInfo.find(
              (op) => op.manifestId === slt.manifestId
            );
            if (!manifest || !opInfo) return null;

            const merged = mergeManifestOptimize(manifest, slt, opInfo);
            return {
              ...merged,
              polylines: "",
              manifestLines: merged.manifestLines.map((manifestLine: any) => ({
                ...manifestLine,
                manifestId: slt.manifestId,
              })),
            };
          })
          .filter((m): m is manifest => m !== null);

        saveManuallyOptimization.mutate(manifestsToApply, {
          onSuccess: (res) => {
            if (res.errorMessage) {
              SnackbarUtils.error("Applying optimized routes failed.");
            } else {
              SnackbarUtils.success(
                "Cherry-pick changes saved and applied successfully"
              );
              setIsCherryPickMode(false);
              onSubmit();
              refetchManifests();
            }
          },
          onError: () => {
            SnackbarUtils.error("Applying optimized routes failed.");
          },
        });
      } else {
        // No rejections, apply directly without reoptimization
        saveManuallyOptimization.mutate(
          optimizedManifests.map((manifest) => {
            return {
              ...manifest,
              polylines: "",
              manifestLines: manifest.manifestLines.map((manifestLine) => {
                return {
                  ...manifestLine,
                  manifestId: manifest.id,
                };
              }),
            };
          }),
          {
            onSuccess: (res) => {
              if (res.errorMessage) {
                SnackbarUtils.error("Applying optimized routes failed.");
              } else {
                SnackbarUtils.success("Cherry-pick changes saved successfully");
                setIsCherryPickMode(false);
                onSubmit();
                refetchManifests();
              }
            },
            onError: () => {
              SnackbarUtils.error("Applying optimized routes failed.");
            },
          }
        );
      }
    } catch (error) {
      console.error("Cherry-pick save error:", error);
      SnackbarUtils.error("Failed to save cherry-pick changes");
      setIsReoptimizing(false);
    }
  };

  return (
    <ModalContent open={open} panelClassWidth="w-full">
      {!isCherryPickMode && (
        <Card
          title={`Optimized ${manifests.length} Route${
            manifests.length > 1 ? "s" : ""
          }`}
          className={clsx(isOpenDetail && "hidden")}
        >
          <div className="flex space-x-4 p-4 h-[calc(100vh-11.625rem)]">
            <div className="w-[26.5rem] flex flex-col">
              {Boolean(unserved.length) && (
                <div className="border border-yellow bg-yellow-light flex py-2.5 px-4 rounded mb-2 items-center">
                  <div>
                    <Icon.InfoFill className="fill-yellow" />
                  </div>
                  <span className="text-sm px-4">
                    Don&apos;t have enough route to deliver all orders.
                    <br />
                    <span className="font-semibold text-yellow-dark">
                      {unserved.length} unallocated orders
                    </span>{" "}
                    is waiting for allocating.
                  </span>
                  <div className="ml-auto">
                    <Common.ViewDetailBtn
                      onClick={() => {
                        setDetailData(unallocatedManifest!);
                        setOpenDetail(true);
                      }}
                    />
                  </div>
                </div>
              )}
              <div className="flex-1">
                <CustomScrollbar>
                  {manifests.map((manifest: manifest, index: number) => {
                    const slt = solutions.find(
                      (sl: any) => sl.manifestId === manifest.id
                    ) as solution;
                    const opInfo = optimizeInfo.find(
                      (opInfoItem) => opInfoItem.manifestId === manifest.id
                    ) as optimizeInfo;
                    return (
                      <OptimizedReviewItem
                        key={index}
                        data-id={manifest.manifestId}
                        onOpenDetail={() =>
                          openDetailModal(slt, manifest, opInfo)
                        }
                        isFirst={!index}
                        manifest={manifest}
                        solution={slt}
                        optimizeInfo={opInfo}
                        capacityType={capacityType}
                        maxCapacityValue={slt?.maxCapacityValue ?? []}
                        totalCapacityValue={slt?.totalCapacityValue ?? []}
                      />
                    );
                  })}
                </CustomScrollbar>
              </div>
            </div>
            <div className="flex-1 rounded bg-neutral-10 flex items-center justify-center relative">
              <div className="z-0 w-full h-full">
                <GoogleMap
                  isFromOptimize={true}
                  storageAddress={storageAddress}
                  manifests={
                    unallocatedManifest
                      ? [...optimizedManifests, unallocatedManifest]
                      : optimizedManifests
                  }
                  onMoveRoute={onMoveUnallocated}
                  manifestIdsCanMove={[manifestIds.UNALLOCATED]}
                />
              </div>
            </div>
          </div>
          <div className="shadow-overline px-4 py-3 flex space-x-2 items-center">
            <div className="flex-1 flex mr-4">
              <Common.Button
                onClick={onClose}
                color="transparent"
                disabled={submitOptimizeRoutes.isLoading}
              >
                Close
              </Common.Button>
            </div>
            <Common.Button onClick={onDiscard} outline color="red">
              Discard
            </Common.Button>
            {shouldShowCherryPickButton && !isCherryPickMode && (
              <Common.Button onClick={handleCherryPick} color="blue" outline>
                Cherry-pick Result
              </Common.Button>
            )}
            <Common.Button
              isLoading={
                submitOptimizeRoutes.isLoading ||
                saveManuallyOptimization.isLoading
              }
              disabled={
                submitOptimizeRoutes.isLoading ||
                saveManuallyOptimization.isLoading
              }
              onClick={handleApply}
            >
              Apply
            </Common.Button>
          </div>
        </Card>
      )}

      {isCherryPickMode && (
        <ModalOptimizedCherryPick
          manifests={manifests}
          solutions={solutions}
          crossZoneLines={crossZoneLines}
          isReoptimizing={isReoptimizing}
          isSaving={saveManuallyOptimization.isLoading}
          reoptimizingRoutesCount={reoptimizingRoutesCount}
          isByTagMode={
            deliveryManifestData?.accountInfo
              ?.routeOptimisationAcceptRejectSetting === 1
          }
          onAcceptNew={handleAcceptNew}
          onKeepOriginal={handleKeepOriginal}
          onUndo={handleUndo}
          onBulkAcceptNew={handleBulkAcceptNew}
          onBulkKeepOriginal={handleBulkKeepOriginal}
          onBack={() => setIsCherryPickMode(false)}
          onSaveAll={handleSaveAllCherryPick}
        />
      )}

      {isOpenDetail && (
        <OptimizedReviewDetail
          onCloseDetail={() => setOpenDetail(false)}
          data={detailData}
        />
      )}
    </ModalContent>
  );
};
